/**
 * P3-09: BuildingBtn —— 建筑按钮组（迁移自 BuildingStackableBtn +
 * BuildingBtnModernController，core.js:1635-2325 + buildings.js:3058-3155）。
 *
 * 覆盖（本步范围：价格、disabled、on/off、自动化标记；sell 链与 staging
 * 归 P3-10，见文件内 TODO 标记）：
 * - 名称：getName 产物直传（label-content + amt 计数/on/off 显示/magneto
 *   幻影/hut 进度/almostLimited 星号，core.js:2053-2075 + buildings.js:3082-3101）——
 *   由引擎 fetchModel 生成，组件不重实现；
 * - 价格/禁用：enabled + resourceIsLimited → disabled/limited（core.js:2099-2111
 *   含 limitBuild/noStackable 规则，引擎侧求值，组件只消费）；
 * - togglable 开关：remove/add 两组 addLinkList dropdown（core.js:1132-1215/
 *   1936-1984），结构/class 钩子逐字对齐（{id}Link、linkContent、dropdown-link），
 *   add1 根链接 "enabled" class（on<val，core.js:2035-2037）；
 * - togglableOnOff 链接：bld-on/bld-off（core.js:1648-1660/1986-1988）；
 * - 自动化标记：typeof isAutomationEnabled == "boolean" → "A"/"*" +
 *   auto-on/auto-off + 原生 title（core.js:1661-1673/1990-1992/2040-2041）；
 * - domNode 态：bldEnabled / bldlackResConvert（val>0 && on>0，
 *   core.js:2024-2032）、font-size:90%（val>9，core.js:2020-2022）；
 * - hasResourceHover（core.js:1678）：mouseover setSelectedObject(model) /
 *   mouseout clearSelectedObject；
 * - 点击：animate（GameButton）→ controller.buyItem(model, event) →
 *   game.render()（BuildingStackableBtn.onClick，core.js:2319-2325）；
 *   Shift/Ctrl 批量与确认弹窗全在引擎 buyItem 内部，组件不拦截。
 *
 * tooltip：ButtonModernHelper.getTooltipHTML 直传（simplePrices=false 的
 * 全结构：desc/自动化行/pollution/almostLimited/价格块/effects/flavor）。
 * 已知差异：旧版定位偏移按钮右侧 300px（core.js:1605）不复刻，见 GameButton
 * 文件头与 TAB-CHECKLIST §2。
 */
import { useCallback, useMemo, useState } from "react";
import type { MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton, GameButtonLink } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { makeModernTooltip, shallowSnapEq, useButtonModel } from "./useButtonModel";
import type { ModernButtonController } from "./useButtonModel";

/** BuildingBtnController 族在 ModernButtonController 之上多出的方法（core.js:1739-1901） */
export interface BuildingBtnControllerLike extends ModernButtonController {
  off(model: unknown, amt?: number): void;
  offAll(model: unknown): void;
  on(model: unknown, amt?: number): void;
  onAll(model: unknown): void;
  sell(event: unknown, model: unknown): void;
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

export interface BuildingBtnSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: boolean | string;
  twoRow: boolean;
  /** metadata.val / on */
  val: number;
  on: number;
  /** metadata.name（small-text 规则用，core.js:1929） */
  metaName: string;
  /** model.togglable（= metadata.togglable，core.js:1674） */
  togglable: boolean;
  /** togglableOnOff 链接（model.togglableOnOffLink，core.js:1648-1660） */
  onOffTitle: string | null;
  onOffCssClass: string | null;
  onOffTooltip: string | null;
  /** 自动化标记（model.toggleAutomationLink，core.js:1661-1673） */
  autoTitle: string | null;
  autoCssClass: string | null;
  autoTooltip: string | null;
  /** bldEnabled / bldlackResConvert 目标 class（core.js:2024-2032） */
  bldStateClass: string | null;
  /** add1 根链接 "enabled" class（on<val，core.js:2035-2037） */
  addEnabled: boolean;
  /** font-size:90%（val>9，core.js:2020-2022） */
  fontSize90: boolean;
  /** model.hasSellLink（= !game.opts.hideSell，buildings.js:3108-3110） */
  hasSellLink: boolean;
  /** model.showSellLink（= metadata.val && hasSellLink，core.js:1645-1646） */
  sellShown: boolean;
  /** (val>9 || name.length>10) && hasSellLink → small-text（core.js:1929-1933） */
  smallText: boolean;
  /** staging 升降级链接标题序列（"^"/"v"，buildings.js:3188-3216；非 staging 为空串） */
  stageKey: string;
}

export interface ModelLinkShape {
  title: string;
  cssClass?: string;
  tooltip?: string;
  handler?: () => void;
}

export function toBuildingSnapshot(model: Record<string, unknown>): BuildingBtnSnapshot {
  const meta = (model.metadata ?? {}) as {
    val?: number;
    on?: number;
    name?: string;
    lackResConvert?: boolean;
  };
  const onOffLink = model.togglableOnOffLink as ModelLinkShape | undefined;
  const autoLink = model.toggleAutomationLink as ModelLinkShape | undefined;
  const stageLinks = (model.stageLinks as ModelLinkShape[] | undefined) ?? [];
  const val = meta.val ?? 0;
  const on = meta.on ?? 0;
  const togglable = !!model.togglable;
  // core.js:2024-2032：有开关链接（togglable 或 togglableOnOff）且 val>0 时，
  // lackResConvert → bldlackResConvert，否则 bldEnabled；class 生效条件 on>0
  const hasToggleLinks = togglable || !!onOffLink;
  return {
    name: model.name as string,
    description: (model.description as string) ?? "",
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    twoRow: !!model.twoRow,
    val,
    on,
    metaName: meta.name ?? "",
    togglable,
    onOffTitle: onOffLink?.title ?? null,
    onOffCssClass: onOffLink?.cssClass ?? null,
    onOffTooltip: onOffLink?.tooltip ?? null,
    autoTitle: autoLink?.title ?? null,
    autoCssClass: autoLink?.cssClass ?? null,
    autoTooltip: autoLink?.tooltip ?? null,
    bldStateClass:
      hasToggleLinks && val > 0 && on > 0
        ? meta.lackResConvert
          ? "bldlackResConvert"
          : "bldEnabled"
        : null,
    addEnabled: val > 0 && on < val,
    fontSize90: val > 9,
    hasSellLink: !!model.hasSellLink,
    sellShown: !!model.showSellLink,
    smallText: !!model.hasSellLink && (val > 9 || (meta.name ?? "").length > 10),
    stageKey: stageLinks.map((l) => l.title).join(""),
  };
}

// ---------------------------------------------------------------------------
// addLinkList dropdown（core.js:1132-1215）
// ---------------------------------------------------------------------------

export interface LinkListItem {
  id: string;
  title: string;
  handler: () => void;
}

/**
 * remove/add 开关链接组：div(float:right) > a.{首项id}Link + div.linkContent
 * （hover 显示）> a.dropdown-link × 剩余项。根链接点击执行首项 handler。
 * 旧版用 dojo mouseover/mouseout 切 display，此处用组件 state 等价表达；
 * 旧版无 animate（addLinkList 的 handler 不走 this.animate，core.js:1171-1178），
 * 保持一致不做闪烁。
 */
export function LinkListDropdown({
  items,
  rootEnabled,
}: {
  items: LinkListItem[];
  /** add1 根链接的 "enabled" class（core.js:2035-2037） */
  rootEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!items.length) {
    return null;
  }
  const [root, ...rest] = items;
  const onLinkClick = (handler: () => void) => (e: MouseEvent) => {
    // core.js:1171-1178 / 1203-1210
    e.stopPropagation();
    e.preventDefault();
    handler();
  };
  return (
    <div
      style={{ float: "right" }}
      onMouseOver={() => setOpen(true)}
      onMouseOut={() => setOpen(false)}
    >
      <a
        href="#"
        className={root.id + "Link" + (rootEnabled ? " enabled" : "")}
        style={{ display: "block", float: "right" }}
        title={root.title}
        onClick={onLinkClick(root.handler)}
        dangerouslySetInnerHTML={{ __html: root.title }}
      />
      <div
        className="linkContent"
        style={{
          display: open ? "block" : "none",
          position: "absolute",
          float: "right",
          marginTop: "35px",
          zIndex: 100,
        }}
      >
        {rest.map((item) => (
          <a
            key={item.id}
            href="#"
            className="dropdown-link"
            style={{ display: "block" }}
            title={item.title}
            onClick={onLinkClick(item.handler)}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BuildingBtn
// ---------------------------------------------------------------------------

export interface BuildingBtnProps {
  /** 建筑 meta name（game.bld 键） */
  building: string;
  /** twoRows 布局（activeGroup all/iw，buildings.js:3415/3446/3455） */
  twoRow?: boolean;
  /**
   * P3-10：staging 建筑（typeof meta.stages == "object"，buildings.js:3440-3448）
   * 用 StagingBldBtnController，渲染 stageLinks "^"/"v" 升降级链
   */
  staging?: boolean;
}

export function BuildingBtn({ building, twoRow = false, staging = false }: BuildingBtnProps) {
  const controller = useMemo(() => {
    const game = getGame();
    const ctors = window.classes?.ui?.btn;
    const Ctor = staging ? ctors?.StagingBldBtnController : ctors?.BuildingBtnModernController;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game) as BuildingBtnControllerLike;
  }, [staging]);

  const createOpts = useCallback(
    (game: GamePage) => {
      // buildings.js:3436-3457：opts.name/description 取 BuildingMeta 包装后的
      // label/description（staging 合并在 P3-10；getName/getDescription 实际消费
      // model.metadata，opts 仅兜底）
      const meta = (
        game.bld as {
          getBuildingExt(name: string): { getMeta(): { label?: string; description?: string } };
        }
      ).getBuildingExt(building).getMeta();
      return {
        name: meta.label ?? building,
        description: meta.description ?? "",
        building,
        type: "buildings",
        twoRow,
      };
    },
    [building, twoRow],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toBuildingSnapshot,
    shallowSnapEq,
  );

  // core.js:2319-2325（BuildingStackableBtn.onClick）：buyItem 后 game.render()
  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
      const game = getGame() as (GamePage & { render?: () => void }) | null;
      game?.render?.();
    },
    [controller, modelRef],
  );

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  const getSelectedObject = useCallback(
    // ButtonModern.getSelectedObject → this.model（core.js:1629-1631）
    () => modelRef.current,
    [modelRef],
  );

  if (!snap) {
    return null;
  }

  // ---- domNode 态（core.js:1924-1933/2020-2032）----
  const domClassName =
    (snap.hasSellLink ? " hasSellLink" : "") +
    (snap.smallText ? " small-text" : "") +
    (snap.bldStateClass ? " " + snap.bldStateClass : "");
  const domStyle = snap.fontSize90 ? { fontSize: "90%" } : undefined;

  // ---- links（旧 renderLinks 顺序：sell(P3-10) → remove/add → onOff → auto）----
  const model = () => modelRef.current;

  const linksChildren: React.ReactNode = (() => {
    const parts: React.ReactNode[] = [];
    // P3-10 sell 链（core.js:1911-1933/2013-2017）：hasSellLink 时渲染，
    // display 随 val>0（showSellLink）；点击直调 controller.sell(event, model)
    // （shift=卖全部确认/50% 退款/undo 全在引擎内部）
    if (snap.hasSellLink) {
      parts.push(
        <GameButtonLink
          key="sell"
          link={{
            title: t("btn.sell.minor"),
            visible: snap.sellShown,
            handler: (event) => controller?.sell(event, model()),
          }}
        />,
      );
    }
    if (snap.togglable) {
      // core.js:1936-1984：remove（-/−25/−all）与 add（+/+25/+all）
      parts.push(
        <LinkListDropdown
          key="remove"
          items={[
            { id: "off1", title: "-", handler: () => controller?.off(model()) },
            { id: "off25", title: "-25", handler: () => controller?.off(model(), 25) },
            {
              id: "offAll",
              title: "-" + t("btn.all.minor"),
              handler: () => controller?.offAll(model()),
            },
          ]}
        />,
        <LinkListDropdown
          key="add"
          rootEnabled={snap.addEnabled}
          items={[
            { id: "add1", title: "+", handler: () => controller?.on(model()) },
            { id: "add25", title: "+25", handler: () => controller?.on(model(), 25) },
            {
              id: "add",
              title: "+" + t("btn.all.minor"),
              handler: () => controller?.onAll(model()),
            },
          ]}
        />,
      );
    }
    if (snap.onOffTitle !== null) {
      // core.js:1986-1988（handler 取最新 model 的闭包，INVENTORY 风险点 6）
      const link: GameButtonLinkModel = {
        title: snap.onOffTitle,
        cssClass: snap.onOffCssClass ?? undefined,
        tooltip: snap.onOffTooltip ?? undefined,
        handler: () => {
          const m = model() as { togglableOnOffLink?: ModelLinkShape } | null;
          m?.togglableOnOffLink?.handler?.();
        },
      };
      parts.push(<GameButtonLink key="onoff" link={link} />);
    }
    if (snap.autoTitle !== null) {
      // core.js:1990-1992/2040-2041（val==0 也照常 update）
      const link: GameButtonLinkModel = {
        title: snap.autoTitle,
        cssClass: snap.autoCssClass ?? undefined,
        tooltip: snap.autoTooltip ?? undefined,
        handler: () => {
          const m = model() as { toggleAutomationLink?: ModelLinkShape } | null;
          m?.toggleAutomationLink?.handler?.();
        },
      };
      parts.push(<GameButtonLink key="auto" link={link} />);
    }
    // P3-10 staging 升降级链（StagingBldBtn.renderLinks，buildings.js:3292-3299：
    // 继承 links 之后逐个 addLink；handler 取最新 model.stageLinks[i] 的闭包）
    if (snap.stageKey) {
      snap.stageKey.split("").forEach((title, i) => {
        parts.push(
          <GameButtonLink
            key={"stage" + i}
            link={{
              title,
              handler: () => {
                const m = model() as { stageLinks?: ModelLinkShape[] } | null;
                m?.stageLinks?.[i]?.handler?.();
              },
            }}
          />,
        );
      });
    }
    return parts;
  })();

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      twoRow={snap.twoRow}
      domClassName={domClassName}
      domStyle={domStyle}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={getSelectedObject}
    >
      {linksChildren}
    </GameButton>
  );
}
